import type { Paginated } from '@/api/_shared/types';

/**
 * Types for the admin logs pages: flow executions (gateway aggregation over flow-observer)
 * and the action-logs audit trail.
 */

import type { ThreadFlowExecution } from '@/api/chat/interfaces';

export const FLOW_EXECUTION_STATUSES = ['STARTED', 'COMPLETED', 'FAILED'] as const;
export type FlowExecutionStatus = (typeof FLOW_EXECUTION_STATUSES)[number];

/** Paginated envelope shared by both endpoints */
export type PaginatedLogsResponse<T> = Paginated<T>;

/** Filters for GET /flow-executions/enriched */
export interface FlowExecutionFilters {
  page: number;
  limit: number;
  /** Tenant (client) ID; omitted for all tenants */
  clientId?: string;
  status?: FlowExecutionStatus;
  /** ISO timestamps on createdAt */
  dateFrom?: string;
  dateTo?: string;
  correlationId?: string;
  sortOrder?: 'asc' | 'desc';
}

/** One flow execution with its client flow and tenant, as returned by the gateway aggregation */
export interface EnrichedFlowExecution extends ThreadFlowExecution {
  triggeredBy?: string | null;
  triggerData?: unknown;
  timings?: Record<string, unknown> | null;
  updatedAt?: string;
  clientFlow: {
    id: string;
    name: string;
    clientId: string;
    companyId?: string | null;
    masterFlow?: { id: string; displayName: string; templateName: string; version: string } | null;
  } | null;
  tenant: { client: { id: string; name: string; key: string } } | null;
}

/** One step event of an execution (GET /flow-observer/api/v1/executions/:id/events) */
export interface FlowExecutionEvent {
  id: string;
  executionId: string;
  nodeId: string | null;
  type: string;
  displayName: string | null;
  clientExplanation: string | null;
  label: string | null;
  data: unknown;
  position: number;
  subPosition: number;
  time: string;
}

/** Filters for GET /action-logs/api/v1/action-logs (ADMIN only) */
export interface ActionLogFilters {
  page: number;
  limit: number;
  search?: string;
  /** Exact companyId, or clientId for tenant-level entries */
  tenant?: string;
  dateFrom?: string;
  dateTo?: string;
}

/** One audit entry; the service only returns these four fields */
export interface ActionLogEntry {
  timestamp: string;
  user_email: string;
  action: string;
  /** ID of the changed entity (client flow or template), not of the log row */
  id: string;
}
